'use client';

import {
  Box,
  Typography,
  IconButton,
  SystemIcon,
} from '@wishket/design-system';
import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { twJoin, twMerge } from 'tailwind-merge';

import {
  ArticleFirstLineProps as ArticleFirstLineData,
  useToolbarScrapPosition,
} from './ArticleDetailTutorial.hooks';

import { Link, BubblePopover } from '@/shared/Components';
import { BubblePopoverProps } from '@/shared/Components/BubblePopover/BubblePopover';
import { PATH } from '@/shared/constants';
import { useDeviceCheck } from '@/shared/hooks';

interface TutorialPopoverProps extends BubblePopoverProps {
  onNext: () => void;
  buttonText: string;
  isLoggedIn: boolean;
}

const PopoverContent = ({
  onNext,
  buttonText,
  content,
  isLoggedIn,
}: TutorialPopoverProps) => {
  return (
    <Box className="flex w-[202px] flex-col gap-1 px-4 py-3">
      <Typography variant={isLoggedIn ? 'body1' : 'body2'}>
        {content}
      </Typography>
      <Typography
        variant={isLoggedIn ? 'body1' : 'body2'}
        className="cursor-pointer self-end font-medium text-primary"
        onClick={onNext}
      >
        {buttonText}
      </Typography>
    </Box>
  );
};

const TutorialPopover = ({
  open,
  variant,
  className,
  content,
  children,
  onNext,
  style,
  buttonText,
  isLoggedIn,
}: TutorialPopoverProps) => {
  return (
    <BubblePopover
      content={
        <PopoverContent
          onNext={onNext}
          content={content}
          buttonText={buttonText}
          isLoggedIn={isLoggedIn}
        />
      }
      open={open}
      variant={variant}
      className={className}
      style={style}
    >
      {children}
    </BubblePopover>
  );
};

interface TutorialTypographyProps {
  isLoggedIn: boolean;
  isDesktop?: boolean;
}

const HightLightTutorialTypography = ({
  isLoggedIn,
  isDesktop,
}: TutorialTypographyProps) => {
  const isVisibleLongPress = !isDesktop;

  return (
    <Typography
      variant={isLoggedIn ? 'body1' : 'body2'}
      className="font-regular break-keep"
    >
      {isLoggedIn ? (
        <>
          <span className=" font-medium text-primary">형광펜</span>으로&nbsp;
        </>
      ) : (
        <SignUpLink />
      )}
      원하는 문장을
      {isVisibleLongPress && ' 길게 눌러'} 저장해 보세요.
    </Typography>
  );
};

const ScrapTutorialTypography = ({ isLoggedIn }: TutorialTypographyProps) => {
  const isVisibleSignUp = !isLoggedIn;

  return isVisibleSignUp ? (
    <Typography variant="body2" className="font-regular break-keep">
      {isVisibleSignUp && <SignUpLink />}
      성장을 위한 콘텐츠를 스크랩 해 보세요.
    </Typography>
  ) : (
    <Typography variant="body1" className="font-regular">
      성장을 위한 콘텐츠를
      <span className="break-keep font-medium text-primary">스크랩</span>해
      보세요.
    </Typography>
  );
};

const SignUpLink = () => {
  const pathname = usePathname();

  return (
    <>
      <span className=" text-primary">
        <Link href={`${PATH.AUTH_SIGNUP}?next=${pathname}`}>회원가입</Link>
      </span>
      {`하고 `}
    </>
  );
};

export const HighlightStep = ({
  isLoggedIn,
  firstLineData,
  onNext,
}: {
  isLoggedIn: boolean;
  firstLineData: ArticleFirstLineData;
  onNext: () => void;
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const firstLineRef = useRef<HTMLDivElement | null>(null);
  const { isDesktop, isMobile } = useDeviceCheck();

  useEffect(() => {
    const firstLineElement = firstLineRef.current;

    if (firstLineElement) {
      requestAnimationFrame(() => {
        firstLineElement.scrollIntoView({
          block: 'center',
          behavior: 'instant',
        });

        requestAnimationFrame(() => {
          setIsMounted(true);
        });
      });
    }
  }, [firstLineData, isDesktop, isMobile, isMounted]);

  return (
    <>
      {firstLineData && (
        <TutorialPopover
          open={true}
          variant={isDesktop ? 'up' : 'down'}
          onNext={onNext}
          content={
            <div ref={firstLineRef}>
              <HightLightTutorialTypography
                isLoggedIn={isLoggedIn}
                isDesktop={isDesktop}
              />
            </div>
          }
          isLoggedIn={isLoggedIn}
          buttonText="다음"
          className="absolute z-30 h-[26px]"
          style={firstLineData.style}
        >
          <Box
            className="absolute z-30 h-[26px] w-full overflow-hidden"
            style={firstLineData.style}
          >
            <Typography
              variant="body1"
              className="whitespace-wrap inline-block w-full bg-w-yellow-300 text-justify"
            >
              {firstLineData.text}
            </Typography>
          </Box>
        </TutorialPopover>
      )}
    </>
  );
};

export const ScrapStep = ({
  onNext,
  isLoggedIn,
}: {
  onNext: () => void;
  isLoggedIn: boolean;
}) => {
  const { isDesktop } = useDeviceCheck();
  const scrapButtonPosition = useToolbarScrapPosition();

  return (
    <>
      {scrapButtonPosition && (
        <TutorialPopover
          open={true}
          variant={isDesktop ? 'right' : 'up'}
          onNext={onNext}
          content={<ScrapTutorialTypography isLoggedIn={isLoggedIn} />}
          buttonText="확인"
          className={twJoin(
            'fixed z-30',
            isDesktop ? 'size-[52px]' : 'size-10'
          )}
          style={scrapButtonPosition}
          isLoggedIn={isLoggedIn}
        >
          <IconButton
            size="lg"
            outline
            className={twMerge(
              'fixed z-40 shrink-0 bg-white',
              isDesktop ? 'p-4' : 'size-10 p-2'
            )}
            style={scrapButtonPosition}
          >
            <SystemIcon name="large_scrap" />
          </IconButton>
        </TutorialPopover>
      )}
    </>
  );
};
